import { useEffect, useState, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import SelectedWaterSystemHeader from "../reuseableComponents/SelectedWaterSystemHeader";
import { VerticalMenu } from "../archive/utah-design-system/react/components/navigation/VerticalMenu.jsx";
import { useWaterSystemSearchModal } from "./WaterSystemSearchModalContext";
import { GET_WATER_SYSTEM_LF_URL_LINK, GET_WATER_SYSTEM_LAT_LONG } from "../../queries/publicQrys/SystemHeaderViewQrys";
import { GET_SAMPLE_SCHEDULE_REPORT } from "../../queries/publicQrys/WaterSystemReportQrys";
import { Modal, ModalTitle, ModalContent, ModalFooter, Button } from '@utahdts/utah-design-system';
import './navigation/SidePanel.css';
import { useHistory } from "react-router-dom";


const SidePanel = (props) => {
  const { setShowSystemSelectModal } = useWaterSystemSearchModal();
  const [errorModalContent, setErrorModalContent] = useState(null);
  const [errorModalTitle, setErrorModalTitle] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { menuItems, selectedWaterSystem } = props;
  const [links, setLinks] = useState([]);
  const [getLFLinkData, { data: lfLinkData, loading: lfLinkLoading, error: lfLinkError }] = useLazyQuery(GET_WATER_SYSTEM_LF_URL_LINK);
  const [getLatLongData, { data: latLongData, loading: latLongLoading, error: latLongError }] = useLazyQuery(GET_WATER_SYSTEM_LAT_LONG);
  const [getSampleScheduleReport, { data: sampleScheduleData }] = useLazyQuery(GET_SAMPLE_SCHEDULE_REPORT);
  const [downloadRequested, setDownloadRequested] = useState(false);
  const history = useHistory();
  const handleCloseModal = () => {
    setShowErrorModal(false);
  };

const processMenuItems = useCallback((items) => {
  return items.map((item) => {
    let linkItem = {
      id: item.code,
      title: item.label,
      childrenMenuType: 'inline',
      children: item.children && item.children.length > 0 ? processMenuItems(item.children) : null,
      actionUrl: { url: item.url_route || '/', openInNewTab: false },
    };

    if (item.route_option === 'newTab') {
      linkItem.actionUrl.openInNewTab = true;
    }

    if (item.route_option === 'csvDownload') {
      linkItem.actionFunction = () => {
        setDownloadRequested(true);
        getSampleScheduleReport();
      };
    }

    if (item.selected_system_required_before_routing && selectedWaterSystem) {
      if (item.url_route) {
        linkItem.actionUrl.url = item.url_route.replace('%systemId%', encodeURIComponent(selectedWaterSystem.systemId));
      }
      if (item.code === 'EQDOCS') {
        getLFLinkData({ variables: { pwsId: selectedWaterSystem.pwsId } });
        if (lfLinkData && lfLinkData.getLFLinkbySystemId) {
          linkItem.actionUrl.url = lfLinkData.getLFLinkbySystemId.urlLink;
        } else if (!lfLinkData || !lfLinkData.getLFLinkbySystemId || !lfLinkData.getLFLinkbySystemId.urlLink) {
          linkItem.actionFunction = () => {
            setErrorModalTitle("No Folder Found");
            setErrorModalContent("There is no folder yet created in Laserfiche. Please contact Rich Peterson or Hunter Payne.");
            setShowErrorModal(true);
          };
        }
      } else if (item.code === 'MAPS') {
        getLatLongData({ variables: { pwsId: selectedWaterSystem.pwsId } });
        if (latLongData && latLongData.getSystemLatLongCoords) {
          const { latitude, longitude } = latLongData.getSystemLatLongCoords;
          linkItem.actionUrl.url = `https://maps.google.com?q=${latitude},${longitude}`;
        } else if (!latLongData || !latLongData.getSystemLatLongCoords) {
          linkItem.actionFunction = () => {
            setErrorModalTitle("No Location Found");
            setErrorModalContent("There is no location set for WS001.");
            setShowErrorModal(true);
          };
        }
      }
    }

    // Handle internal routes vs external routes
    // Skip routing logic for items that are just category headers (url_route is null and has children)
    if (item.url_route === null && item.children && item.children.length > 0) {
      // This is a category header/section - don't set any routing
      linkItem.actionUrl = null;
      linkItem.actionFunction = null;
    } else if (item.use_react_routing && item.url_route) {
      // For internal routes that require a system but don't have one selected:
      // Just open the modal, don't navigate yet
      if (item.selected_system_required_before_routing && !selectedWaterSystem) {
        linkItem.actionUrl = null;
        linkItem.actionFunction = () => { 
          setShowSystemSelectModal(true); 
        };
      } else {
        // For internal routes with a system selected (or no system requirement):
        // Do React Router navigation
        linkItem.actionUrl = null;
        linkItem.actionFunction = (e) => {
          e.preventDefault();
          e.stopPropagation();
          history.push(item.url_route);
        };
      }
    } else if (item.selected_system_required_before_routing && !selectedWaterSystem) {
      // For external routes that require a system but don't have one selected:
      // Just open the modal
      linkItem.actionFunction = () => { 
        setShowSystemSelectModal(true); 
      };
    }
    
    return linkItem;
  });
}, [selectedWaterSystem, setShowSystemSelectModal, getLFLinkData, lfLinkData, setErrorModalTitle, setErrorModalContent, setShowErrorModal, getLatLongData, latLongData, getSampleScheduleReport, history]);

// Download CSV when data is ready and download was requested
useEffect(() => {
  if (downloadRequested && sampleScheduleData && sampleScheduleData.getSampleScheduleReportCsv) {
    const reportObj = sampleScheduleData.getSampleScheduleReportCsv;
    const csvFileStream = reportObj.stream.replace(/\r?\n/g, '\r\n');
    const csvFileName = reportObj.filename;
    const blob = new Blob([csvFileStream], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = csvFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    setDownloadRequested(false);
  }
}, [downloadRequested, sampleScheduleData]);

useEffect(() => {
  if ( menuItems && Array.isArray(menuItems)) {
    setLinks(processMenuItems(menuItems));
  }
}, [menuItems, selectedWaterSystem, latLongData, latLongLoading, latLongError, getLatLongData, lfLinkData, lfLinkLoading, lfLinkError, getLFLinkData, setShowSystemSelectModal, processMenuItems]);

  return (
    <>
    {showErrorModal && (
      <div className="utah-design-system">
        <Modal
          className="modal--medium"
          onClose={handleCloseModal}
          onEscape={handleCloseModal}
        >
          <ModalTitle>{errorModalTitle}</ModalTitle>
          <ModalContent id="WaterSystemRedirect-ErrorModal-Content">
            <p>{errorModalContent}</p>
          </ModalContent>
          <ModalFooter id="WaterSystemRedirect-ErrorModal-Footer">
            <Button onClick={handleCloseModal}>Close</Button>
          </ModalFooter>
        </Modal>
      </div>
    )}
  
    <SelectedWaterSystemHeader selectedWaterSystem={selectedWaterSystem} />
    <div className="utah-design-system">
    <VerticalMenu
      menus={[{menuItems: links}]}
    />
    </div>
    </>
  );
};

export default SidePanel;